import React, {ReactNode} from 'react';
import {Button} from '@mui/material';
import {Typography} from '@mui/material';
import AurLoadingIndicator from '../aur-loading-indicator/AurLoadingIndictor';

interface ILoadingButtonProps {
  label?: string | undefined;
  loading: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  size?: 'Large' | 'Medium' | 'Small';
  classNames?: string;
  title?: string;
  autoFocus?: boolean;
  tabIndex?: number;
  ariaDescribedBy?: string;
  name?: string;
  buttonType?: 'button' | 'submit' | 'reset';
  ariaLabel?: string;
  ariaDescription?: string;
  progressCircleSize?: number;
  dataTestId?: string;
  disabled?: boolean;
  startIcon?: ReactNode;
  minWidth?: number;
}

export const AurLoadingButton = ({
  label,
  loading = true,
  onClick,
  size = 'Large',
  classNames = '',
  title = '',
  autoFocus = false,
  tabIndex,
  ariaDescribedBy,
  name = '',
  buttonType = 'button',
  ariaLabel,
  ariaDescription = '',
  progressCircleSize,
  dataTestId,
  disabled,
  startIcon,
  minWidth,
}: ILoadingButtonProps) => {
  const progressSize =
    progressCircleSize != null
      ? progressCircleSize
      : size === 'Large'
        ? 24
        : size === 'Medium'
          ? 20
          : 18;

  return (
    <>
      <Button
        className={`aur-loading-button button${size}_size ${classNames} ${
          disabled ? 'mui-button-disabled' : ''
        }`}
        variant="contained"
        aria-disabled={disabled ?? loading}
        disableRipple={disabled ?? loading}
        onClick={(e) => {
          if (disabled) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          onClick?.(e);
        }}
        aria-description={ariaDescription}
        title={title}
        tabIndex={tabIndex}
        autoFocus={autoFocus}
        aria-label={ariaLabel || label}
        disabled={disabled ?? loading}
        aria-describedby={disabled || loading ? 'btn-info' : ariaDescribedBy}
        focusVisibleClassName={name}
        type={buttonType}
        data-testid={dataTestId}
        startIcon={startIcon}
        style={{
          minWidth: `${minWidth && minWidth}px`,
        }}
        sx={{
          '& .MuiButton-startIcon svg': {
            opacity: disabled ? 0.5 : 1,
            filter: disabled ? 'grayscale(100%)' : 'none',
          },
        }}
      >
        {loading && (
          <AurLoadingIndicator size={progressSize} className="progress-icon" />
        )}
        {label && (
          <Typography className={`typo-text button${size}_size button-text`}>
            {label}
          </Typography>
        )}
      </Button>
      <span
        id="btn-info"
        role="button"
        className="sr-only"
        tabIndex={(disabled || loading) && ariaDescription ? 0 : -1}
        aria-label={`${ariaLabel || label} button`}
        aria-disabled={disabled ?? loading}
        aria-description={ariaDescription}
      ></span>
    </>
  );
};
